import 'core-js';
import 'isomorphic-fetch';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import AppRoot from './AppRoot';
import GraphQLClientFactory from './lib/GraphQLClientFactory';
import config from './temp/config';
import {
  resolveLanguageFromContextData,
  resolveLanguageFromConfig,
} from './lib/LanguageResolvers';
import { matchRoute } from './lib/routerUtils';
import { AppStateProvider } from './lib/AppStateProvider';
import { GlobalStyle } from './globalStyles';

// Resolve the current matched router path
function resolveRoutePath() {
  const matchedRoute = matchRoute(window.location.pathname);
  return (matchedRoute && matchedRoute.params.sitecoreRoute) || '/';
}

// Resolve the current matched router path
function resolveItemPathFromSSR(route, ssrState, lang) {
  const routePath = resolveRoutePath();
  return ssrState && ssrState.routeMap
    ? ssrState.routeMap[lang][routePath]
    : routePath;
}

function resolveLanguage(ssrState) {
  // If SSR data exists, we try to extract the language from that data.
  let language = resolveLanguageFromContextData(ssrState);

  // If a language parameter is found in the route URL and the parameter has a different value
  // than specified in SSR data, use the parameter value. This shouldn't ever happen, but
  // anything is possible.
  const matchedRoute = matchRoute(window.location.pathname);
  if (
    matchedRoute &&
    matchedRoute.params &&
    matchedRoute.params.lang &&
    language !== matchedRoute.params.lang
  ) {
    language = matchedRoute.params.lang;
  }

  // If no language could be found in SSR data or route URL, then fallback to the value defined in config.
  if (!language) {
    language = resolveLanguageFromConfig(config);
  }

  return language;
}

/*
  SSR Data
  If we're running in a server-side rendering scenario,
  the server will provide JSON in the #__JSS_STATE__ element
  for us to acquire the initial state to run with on the client.

  This enables us to skip a network request to load up the layout data.
  We are emitting a quiescent script with JSON so that we can take advantage
  of JSON.parse()'s speed advantage over parsing full JS, and enable
  working without needing `unsafe-inline` in Content Security Policies.

  SSR is initiated from /server/server.js.
*/

function resolveInitialAppState() {
  const ssrRawJson = document.getElementById('__JSS_STATE__');
  const ssrState = ssrRawJson ? JSON.parse(ssrRawJson.innerHTML) : null;
  const routePath = resolveRoutePath();
  /*
  Language Resolving
  Attempt to resolve the app language prior to rendering the app.
  */
  const language = resolveLanguage(ssrState);
  const itemPath = resolveItemPathFromSSR(routePath, ssrState, language);

  const appState = {
    routePath,
    language,
    itemPath,
  };

  if (ssrState) {
    const { GRAPHQL_STATE, dictionary, routeMap, ...routeData } = ssrState;

    return {
      ...appState,
      initialGraphQLState: GRAPHQL_STATE || null,
      dictionary: dictionary || null,
      routeData: routeData || null,
      routeMap: routeMap || null,
    };
  }

  return appState;
}

function renderApp() {
  const initialState = resolveInitialAppState();
  /*
    GraphQL Data
    The GraphQL Client needs to be initialized to make GraphQL available to the JSS app.
    Not using GraphQL? Remove this, and the ClientContext from `AppRoot`.
  */
  // The GraphQL Client supports SSR of GraphQL queries, so like JSS SSR, it has an object we can
  // hydrate the client cache from to avoid needing to re-run GraphQL queries after the SSR page loads.
  const graphQLClient = GraphQLClientFactory(
    config.graphQLEndpoint,
    initialState.initialGraphQLState
  );

  // HTML element to place the app into
  const rootElement = document.getElementById('root');

  // Create the Router component that will be used for client-side routing.
  const Router = (routerProps) => (
    <BrowserRouter>{routerProps.children}</BrowserRouter>
  );

  // GTM events params
  const pageType = initialState?.routeData?.sitecore?.route?.fields?.gtmPageType?.fields?.value?.value;
  const { language } = initialState;

  // when React initializes from a SSR-based initial state, you need to render with `hydrate` instead of `render`
  const renderFunction = initialState.routeData
    ? ReactDOM.hydrate
    : ReactDOM.render;

  renderFunction(
    <AppStateProvider initialState={initialState}>
      <GlobalStyle />
      <AppRoot pageType={pageType} language={language} Router={Router} graphQLClient={graphQLClient} />
    </AppStateProvider>,
    rootElement
  );
}

renderApp();
